import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './MarketSpyResultDetail.module.css'
import { MarketSpySelectionSection } from './MarketSpySelectionSection'
import { MarketSpySection } from './MarketSpySection'
import { HeaderSection } from './HeaderSection'
import { ResultSection } from './ResultSection'
import { RecommendationsSection } from './RecommendationsSection'
import { SummarySection } from './SummarySection'
import { GraphsSection } from './GraphsSection'
import { HostServicesSection } from './HostServicesSection'
import { useReadMonitorRunResultGroupedByMonitorIdQuery } from 'services/monitorRunResult'
import { DateTime } from 'luxon'
import { findClosestDate } from 'shared/lib/findClosestDate'

interface SpyResultDetailCardProps {
    marketSpyId?: number
}

export const MarketSpyResultDetail = (props: SpyResultDetailCardProps): JSX.Element => {
    const {
        marketSpyId
    } = props

    const processDateString = (dateString: any) => {
        return DateTime.fromISO(dateString);
    };

    const findSuitableDay = () => {
        const closest = findClosestDate(readMonitorRunResultGroupedByMonitorIdData.list, ({ checkinUtc }: { checkinUtc: any }) => processDateString(checkinUtc));
        return closest
    }

    const navigate = useNavigate()
    const [monitorLaunchTask, setMonitorLaunchTask] = useState<any>(undefined)
    const [marketSpyRunResult, setMarketSpyRunResult] = useState<any>(undefined)
    const [readMonitorRunResultGroupedByMonitorIdQueryParams, setReadMonitorRunResultGroupedByMonitorIdQueryParams] = useState<any>({
        monitorId: marketSpyId
    })

    const [readMonitorRunResultGroupedByMonitorIdQueryOptions, setReadMonitorRunResultGroupedByMonitorIdQueryOptions] = useState<any>({
        skip: marketSpyId === undefined,
        pollingInterval: 5000,
    })

    const {
        data: readMonitorRunResultGroupedByMonitorIdData,
        isLoading: readMonitorRunResultGroupedByMonitorIdIsLoading,
        isFetching: readMonitorRunResultGroupedByMonitorIdIsFetching,
    } = useReadMonitorRunResultGroupedByMonitorIdQuery(
        readMonitorRunResultGroupedByMonitorIdQueryParams,
        readMonitorRunResultGroupedByMonitorIdQueryOptions
    )

    useEffect(() => {
        setReadMonitorRunResultGroupedByMonitorIdQueryParams((prevValue: any) => {
            return {
                ...prevValue,
                monitorId: marketSpyId
            }
        });

        setReadMonitorRunResultGroupedByMonitorIdQueryOptions((prevValue: any) => {
            return {
                ...prevValue,
                skip: marketSpyId === undefined,
            }

        })
    }, [
        marketSpyId
    ])

    useEffect(() => {
        setMonitorLaunchTask(undefined)
        setMarketSpyRunResult(undefined)
    }, [
        marketSpyId
    ])

    useEffect(() => {
        if (readMonitorRunResultGroupedByMonitorIdData === undefined) {
            return
        }

        if (monitorLaunchTask !== undefined) {
            return
        }

        if (readMonitorRunResultGroupedByMonitorIdData.list.length === 0) {
            return
        }

        const selectedValue = findSuitableDay()
        handleSelectedMonitorLaunchTaskChange({           
            monitorLaunchTask: selectedValue
        })
    }, [
        readMonitorRunResultGroupedByMonitorIdData
    ])

    const handleMarketSpyChange = (event: any) => {
        navigate(`/spy/${event.target.value}/results`, {
            preventScrollReset: true
        })
    }

    const handleSelectedMonitorLaunchTaskChange = (event: any) => {
        setMonitorLaunchTask(event)
        setMarketSpyRunResult(event.monitorLaunchTask)
    }

    if (marketSpyId === undefined) {
        return (
            <div className={styles.MarketSpyResultDetail_Root}>
                <MarketSpySelectionSection
                    marketSpyId={marketSpyId}
                    onMarketSpyChange={handleMarketSpyChange}
                />
            </div>
        )
    }

    return (
        <div className={styles.MarketSpyResultDetail_Root}>
            <MarketSpySelectionSection
                marketSpyId={marketSpyId}
                onMarketSpyChange={handleMarketSpyChange}
            />

            <div className={styles.MarketSpyResultDetail_SectionListContainer}>
                <HeaderSection
                    marketSpyId={marketSpyId}
                />
                <div className={styles.MarketSpyResultDetail_MainSectionListContainer}>
                    <MarketSpySection
                        marketSpyId={marketSpyId}
                    />
                    <ResultSection
                        marketSpyId={marketSpyId}
                        monitorLaunchTaskResult={marketSpyRunResult}
                        onSelectedMonitorLaunchTaskChange={handleSelectedMonitorLaunchTaskChange}
                    />
                    <SummarySection
                        marketSpyId={marketSpyId}
                        marketSpyRunResult={marketSpyRunResult}
                    />
                    <GraphsSection
                        marketSpyId={marketSpyId}
                        marketSpyRunResult={marketSpyRunResult}
                    />
                    <RecommendationsSection
                        marketSpyId={marketSpyId}
                        onSelectedMonitorLaunchTaskChange={handleSelectedMonitorLaunchTaskChange}
                        marketSpyRunResult={marketSpyRunResult}
                    />
                    <HostServicesSection
                        marketSpyId={marketSpyId}
                        monitorLaunchTaskResult={marketSpyRunResult}
                    />
                </div>
            </div>
        </div>
    )
}
